import React, { useState, useEffect, useCallback } from 'react';
import { createAppointmentInboxItems } from '../services/appointmentService';
import { db } from '../firebase';
import PageHeader from './ui/PageHeader';
import '../styles/homeQuietLounge.css';
import { AnalyticsIcon, PhoneIcon, PartnerIcon, CalendarIcon, CommunityIcon, SettingsIcon, ChatIcon, MailIcon, SuccessIcon, NotificationIcon, DocumentIcon, GoalIcon } from './icons';
import { Search, Mic, Trophy, Smartphone, Heart } from 'lucide-react';
import ScrollIndicator from './ui/ScrollIndicator';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  updateDoc, 
  doc, 
  deleteDoc,
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { UserProfile } from '../types';
import ReportButton from './ReportButton';
import { formatRangeNl } from '../utils/eventDisplay';
import { navigateTo, openConnectieFlow, openChat, openAgenda } from '../utils/navigationHelper';
import ConversationAnalysisItem from './inbox/ConversationAnalysisItem';
import { getUserAnalyses } from '../services/conversationAnalysisService';
import { ConversationAnalysis } from '../types/conversationAnalysis.types';
import { counterProposal as serviceCounterProposal, acceptProposal as serviceAcceptProposal, MeetingProposal } from '../services/proposalService';

interface InboxItem {
  id: string;
  userId: string;
  type:
    | 'reminder'
    | 'confirmation'
    | 'summary'
    | 'notification'
    | 'system'
    | 'chat'
    | 'matchmaking'
    | 'partnership'
    | 'podcast_invite'
    | 'connection_request'
    | 'meeting_proposal'
    | 'proposal_accepted'
    | 'planning_pending'
    | 'proposal_received'
    | 'counter_received'
    | 'appointment_confirmed'
    | 'group_invitation'
    | 'event_invitation';
  category:
    | 'call'
    | 'meeting'
    | 'podcast'
    | 'connection'
    | 'system'
    | 'achievement'
    | 'chat'
    | 'matchmaking'
    | 'partnership'
    | 'social'
    | 'planning'
    | 'afspraak';
  phase?: string;
  title: string;
  message: string;
  subtitle?: string;
  isRead: boolean;
  isArchived?: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: Date;
  data?: {
    proposalId?: string;
    connectionId?: string;
    slots?: Array<{ start: string; end: string }>;
    eventId?: string;
    start?: string;
    end?: string;
    planningOwnerId?: string;
    otherUserName?: string;
    /** Physical meeting location name — set by appointmentService */
    location?: string | null;
    /** Reminder minutes before the appointment — set by appointmentService */
    reminderMinutesBefore?: number;
  };
  fromUserId?: string;
  fromUserName?: string;
  fromPhone?: string;
  actionData?: {
    actionType?: 'view_call' | 'join_meeting' | 'accept_invitation' | 'view_summary' | 'reply_chat' | 'accept_match' | 'view_partnership' | 'join_podcast' | 'accept_connection';
    actionUrl?: string;
    actionText?: string;
    callId?: string;
    meetingId?: string;
    connectionId?: string;
    chatId?: string;
    matchId?: string;
    partnershipId?: string;
    podcastId?: string;
    connectionName?: string;
    otherUserId?: string;
    changeRequestId?: string;
    senderName?: string;
    data?: {
      proposalId?: string;
      connectionId?: string;
      slots?: Array<{ start: string; end: string }>;
    };
  };
  metadata?: {
    callDuration?: number;
    participants?: string[];
    eventDate?: Date;
    summaryData?: any;
    senderName?: string;
    senderPhoto?: string;
    matchScore?: number;
    partnershipType?: string;
    podcastTitle?: string;
    color?: string;
    isRecurring?: boolean;
    repeat?: string;
    // Fields used for meeting change requests
    oldStartTime?: any;
    newStartTime?: any;
    reason?: string;
    // Fields used for meeting proposals (Fase 8)
    status?: 'pending' | 'handled';
    handledAt?: Date;
    acceptedSlot?: { start: string; end: string };
    eventId?: string;
  };
}

interface CallSummary {
  id: string;
  callId: string;
  participants: string[];
  participantNames: string[];
  duration: number;
  startTime: Date;
  endTime: Date;
  topics: string[];
  keyPoints: string[];
  actionItems: string[];
  nextSteps: string[];
  sentiment: 'positive' | 'neutral' | 'negative';
  quality: 'excellent' | 'good' | 'fair' | 'poor';
  aiGenerated: boolean;
  createdAt: Date;
}

interface InboxPageProps {
  userProfile: UserProfile | null;
  mobileTabIndex?: number;
}

const InboxPage: React.FC<InboxPageProps> = ({ userProfile, mobileTabIndex = 0 }) => {
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'reminders' | 'summaries' | 'confirmations' | 'matchmaking' | 'partnerships' | 'podcast_invites' | 'connection_requests' | 'planning' | 'afspraken' | 'samenvattingen' | 'archived' | 'event_invitations'>('all');
  const [inboxItems, setInboxItems] = useState<InboxItem[]>([]);
  const [callSummaries, setCallSummaries] = useState<CallSummary[]>([]);
  const [analyses, setAnalyses] = useState<ConversationAnalysis[]>([]);
  const [loadingAnalyses, setLoadingAnalyses] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedItem, setSelectedItem] = useState<InboxItem | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  // Meeting change request state
  const [showChangeResponseModal, setShowChangeResponseModal] = useState(false);
  const [changeResponse, setChangeResponse] = useState<{ message: string; accept: boolean }>({
    message: '',
    accept: false
  });
  const [respondingToChange, setRespondingToChange] = useState<InboxItem | null>(null);
  // Proposal accept/decline state
  const [expandedProposal, setExpandedProposal] = useState<string | null>(null);
  const [processingProposal, setProcessingProposal] = useState<string | null>(null);
  // Group invitation accept/decline state
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);

  useEffect(() => {
    if (mobileTabIndex === 0) setActiveTab('all');
    if (mobileTabIndex === 1) setActiveTab('unread');
    if (mobileTabIndex === 2) setActiveTab('confirmations');
  }, [mobileTabIndex]);

  const handleAcceptGroupInvitation = async (item: InboxItem) => {
    const { invitationId, entityId, role, entityType } = (item.actionData ?? {}) as Record<string, string>;
    if (!invitationId || !entityId || !userProfile?.uid) return;
    setProcessingInvitation(item.id);
    try {
      const invSnap = await getDoc(doc(db, 'entityInvitations', invitationId));
      const inv = invSnap.exists() ? invSnap.data() as Record<string, unknown> : null;
      const resolvedRole = (inv?.role as string) || role || 'member';
      const memberPayload: Record<string, unknown> = {
        entityId,
        userId: userProfile.uid,
        role: resolvedRole,
        createdAt: serverTimestamp(),
        invitationId,
      };
      if (resolvedRole === 'organizer' && inv?.capabilities && typeof inv.capabilities === 'object') {
        memberPayload.capabilities = inv.capabilities;
      }
      await setDoc(doc(db, 'entityMembers', `${entityId}_${userProfile.uid}`), memberPayload);
      // Update invitation status
      await updateDoc(doc(db, 'entityInvitations', invitationId), { status: 'accepted' });
      // Mark inbox item as read and archive it
      await updateDoc(doc(db, 'inboxItems', item.id), { isRead: true, isArchived: true });
      setInboxItems(prev => prev.filter(i => i.id !== item.id));

      // Navigate directly to accepted group so the user lands in context immediately.
      let resolvedType = entityType;
      if (!resolvedType) {
        try {
          const entitySnap = await getDoc(doc(db, 'entities', entityId));
          if (entitySnap.exists()) {
            resolvedType = String((entitySnap.data() as any)?.type || '');
          }
        } catch {}
      }
      const hubType = resolvedType === 'networkclub' ? 'networkclub-hub' : 'mastermind-hub';
      localStorage.setItem('activeEntityId', entityId);
      window.dispatchEvent(new CustomEvent('activeEntityChanged', { detail: { entityId } }));
      window.location.hash = `#${hubType}`;
    } catch (err) {
      console.error('Error accepting invitation:', err);
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDeclineGroupInvitation = async (item: InboxItem) => {
    const { invitationId } = (item.actionData ?? {}) as Record<string, string>;
    if (!invitationId) return;
    setProcessingInvitation(item.id);
    try {
      await updateDoc(doc(db, 'entityInvitations', invitationId), { status: 'declined' });
      await updateDoc(doc(db, 'inboxItems', item.id), { isRead: true, isArchived: true });
      setInboxItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      console.error('Error declining invitation:', err);
    } finally {
      setProcessingInvitation(null);
    }
  };

  // Reminder settings per inbox item (keyed by item.id, value = minutes before)
  const [reminderSettings, setReminderSettings] = useState<Record<string, number>>({});
  // Track which item is currently saving its reminder to prevent double saves
  const [savingReminder, setSavingReminder] = useState<string | null>(null);
  // Per-item save error feedback (keyed by item.id)
  const [reminderErrors, setReminderErrors] = useState<Record<string, string>>({});

  // New async proposal/counter state
  const [counterModalItemId, setCounterModalItemId] = useState<string | null>(null);
  const [counterSlotInputs, setCounterSlotInputs] = useState<Array<{ date: string; startTime: string; endTime: string }>>([
    { date: '', startTime: '', endTime: '' },
    { date: '', startTime: '', endTime: '' },
    { date: '', startTime: '', endTime: '' },
  ]);
  const [sendingCounter, setSendingCounter] = useState(false);
  const [processingNewProposal, setProcessingNewProposal] = useState<string | null>(null);

  // Chat state
  const [chatMessages, setChatMessages] = useState<Array<{
    id: string;
    senderId: string;
    senderName: string;
    content: string;
    timestamp: Date;
  }>>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedChatUser, setSelectedChatUser] = useState<{
    id: string;
    name: string;
    avatar?: string;
  } | null>(null);

  // Load analyses when tab is samenvattingen
  useEffect(() => {
    if (activeTab === 'samenvattingen' && userProfile?.uid) {
      loadAnalyses();
    }
  }, [activeTab, userProfile?.uid]);

  const loadAnalyses = async () => {
    if (!userProfile?.uid) return;
    
    try {
      setLoadingAnalyses(true);
      const data = await getUserAnalyses(userProfile.uid, 20);
      setAnalyses(data);
    } catch (error) {
      console.error('Error loading analyses:', error);
    } finally {
      setLoadingAnalyses(false);
    }
  };

  const loadInboxData = useCallback(async () => {
    try {
      setLoading(true);
      // Clear any stale per-item reminder errors when reloading — the items
      // themselves will be replaced, so errors from old items would be orphaned.
      setReminderErrors({});

      // Load inbox items — orderBy is intentionally omitted here so the query
      // works with only the single-field auto-index on userId. Sorting is done
      // client-side. The composite index (userId ASC, createdAt DESC) is
      // defined in firestore.indexes.json and will be used automatically once
      // deployed via `firebase deploy --only firestore:indexes`.
      const inboxQuery = query(
        collection(db, 'inboxItems'),
        where('userId', '==', userProfile?.uid)
      );
      const inboxSnapshot = await getDocs(inboxQuery);
      const inboxData = (inboxSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        metadata: {
          ...doc.data().metadata,
          eventDate: doc.data().metadata?.eventDate?.toDate()
        }
      })) as InboxItem[]).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setInboxItems(inboxData);
      const unread = inboxData.some(item => !item.isRead && !item.isArchived);
      const unreadCount = inboxData.filter(item => !item.isRead && !item.isArchived).length;
      window.dispatchEvent(new CustomEvent('inboxUnreadChanged', { detail: { hasUnread: unread, unreadCount } }));

      // Load call summaries
      const summariesQuery = query(
        collection(db, 'callSummaries'),
        where('participants', 'array-contains', userProfile?.uid),
        orderBy('createdAt', 'desc')
      );
      const summariesSnapshot = await getDocs(summariesQuery);
      const summariesData = summariesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startTime: doc.data().startTime?.toDate() || new Date(),
        endTime: doc.data().endTime?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as CallSummary[];
      
      setCallSummaries(summariesData);

      // Load chat messages
      const chatMessagesQuery = query(
        collection(db, 'chatMessages'),
        where('receiverId', '==', userProfile?.uid),
        where('isRead', '==', false),
        orderBy('createdAt', 'desc')
      );
      const chatMessagesSnapshot = await getDocs(chatMessagesQuery);
        const chatMessagesData = chatMessagesSnapshot.docs.map(doc => ({
          id: `chat_${doc.id}`,
          userId: userProfile?.uid || '',
          type: 'chat' as const,
          category: 'chat' as const,
          title: `Nieuw bericht van ${doc.data().senderName}`,
          message: doc.data().content,
          isRead: false,
          priority: 'medium' as const,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          actionData: {
            actionType: 'reply_chat' as const,
            actionText: 'Antwoorden',
            chatId: doc.data().chatId
          },
          metadata: {
            senderName: doc.data().senderName,
            senderPhoto: doc.data().senderPhoto
          }
        })) as InboxItem[];

      // Load connection requests
      const connectionRequestsQuery = query(
        collection(db, 'connectionRequests'),
        where('toUserId', '==', userProfile?.uid),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const connectionRequestsSnapshot = await getDocs(connectionRequestsQuery);
        const connectionRequestsData = connectionRequestsSnapshot.docs.map(doc => ({
          id: `connection_${doc.id}`,
          userId: userProfile?.uid || '',
          type: 'connection_request' as const,
          category: 'connection' as const,
          title: `Nieuwe connectie aanvraag van ${doc.data().senderName}`,
          message: `${doc.data().senderName} wil met je connecten. Bekijk hun profiel en accepteer of weiger de aanvraag.`,
          isRead: false,
          priority: 'medium' as const,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          actionData: {
            actionType: 'accept_connection' as const,
            actionText: 'Bekijk Aanvraag',
            connectionId: doc.id
          },
          metadata: {
            senderName: doc.data().senderName,
            senderPhoto: doc.data().senderPhoto
          }
        })) as InboxItem[];

      // Load meeting requests
      const meetingRequestsQuery = query(
        collection(db, 'meetingRequests'),
        where('toUserId', '==', userProfile?.uid),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const meetingRequestsSnapshot = await getDocs(meetingRequestsQuery);
        const meetingRequestsData = meetingRequestsSnapshot.docs.map(doc => ({
          id: `meeting_${doc.id}`,
          userId: userProfile?.uid || '',
          type: 'notification' as const,
          category: 'meeting' as const,
          title: `Meeting verzoek van ${doc.data().fromUserId}`,
          message: `${doc.data().title} - ${doc.data().description}`,
          isRead: false,
          priority: 'high' as const,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          actionData: {
            actionType: 'accept_invitation' as const,
            actionText: 'Bekijk Verzoek',
            meetingId: doc.id
          },
          metadata: {
            eventDate: doc.data().proposedDate?.toDate()
          }
        })) as InboxItem[];

      // Combine all notifications
      const allNotifications = [...inboxData, ...chatMessagesData, ...connectionRequestsData, ...meetingRequestsData];
      setInboxItems(allNotifications);

    } catch (error) {
      console.error('Error loading inbox data:', error);
    } finally {
      setLoading(false);
    }
  // Re-create when the user changes so we always query the correct userId
  }, [userProfile?.uid]);

  useEffect(() => {
    if (userProfile?.uid) {
      loadInboxData();
    }
  }, [userProfile?.uid, loadInboxData]);

  useEffect(() => {
    const handleOpenInboxTab = (event: Event) => {
      const customEvent = event as CustomEvent<{ tab: string }>;
      const tab = customEvent.detail?.tab;

      if (tab && ['all', 'unread', 'reminders', 'summaries', 'confirmations', 'matchmaking', 'partnerships', 'podcast_invites', 'connection_requests', 'planning', 'afspraken'].includes(tab)) {
        setActiveTab(tab as any);
      }

      // Reload inbox data so newly created appointment_confirmed items are visible.
      // Guard: only reload when the user is authenticated — if userProfile is not
      // yet available (race between navigation and auth state), loadInboxData would
      // query with undefined userId and return no results.
      if (userProfile?.uid) {
        loadInboxData().catch(err => console.warn('[InboxPage] reload after openInboxTab failed:', err));
      }
    };

    window.addEventListener('openInboxTab', handleOpenInboxTab as EventListener);
    return () => window.removeEventListener('openInboxTab', handleOpenInboxTab as EventListener);
  }, [loadInboxData, userProfile?.uid]);

  const markAsRead = async (itemId: string) => {
    try {
      const item = inboxItems.find(item => item.id === itemId);
      if (!item) return;

      await updateDoc(doc(db, 'inboxItems', itemId), {
        isRead: true
      });

      setInboxItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, isRead: true } : item
      ));
    } catch (error) {
      console.error('Error marking item as read:', error);
    }
  };

  const deleteItem = async (itemId: string) => {
    try {
      await deleteDoc(doc(db, 'inboxItems', itemId));
      setInboxItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const archiveItem = async (itemId: string) => {
    try {
      await updateDoc(doc(db, 'inboxItems', itemId), { isArchived: true });
      setInboxItems(prev => prev.map(item => item.id === itemId ? { ...item, isArchived: true } : item));
    } catch (error) {
      console.error('Error archiving item:', error);
    }
  };

  const unarchiveItem = async (itemId: string) => {
    try {
      await updateDoc(doc(db, 'inboxItems', itemId), { isArchived: false });
      setInboxItems(prev => prev.map(item => item.id === itemId ? { ...item, isArchived: false } : item));
    } catch (error) {
      console.error('Error unarchiving item:', error);
    }
  };

  /**
   * Update the reminder on the linked calendarEvent for an appointment_confirmed
   * inbox item. Reads item.data.eventId to find the event, then updates:
   *   reminderMinutesBefore, reminderAt (recalculated), reminderSent: false
   */
  const saveReminderSetting = async (item: InboxItem, minutesBefore: number) => {
    // Type guard: both eventId and start must be present and valid
    const eventId = item.data?.eventId;
    const startIso = item.data?.start;
    if (!eventId || typeof eventId !== 'string' || !startIso || typeof startIso !== 'string') {
      console.warn('[InboxPage] saveReminderSetting: missing or invalid eventId/start on item', item.id);
      return;
    }

    // Validate date before computing reminderAt
    const startTime = new Date(startIso);
    if (isNaN(startTime.getTime())) {
      console.warn('[InboxPage] saveReminderSetting: invalid start date:', startIso);
      setReminderErrors(prev => ({ ...prev, [item.id]: 'Ongeldige afspraakdatum — herinnering kon niet worden opgeslagen.' }));
      return;
    }

    if (savingReminder === item.id) return; // guard against double save
    setSavingReminder(item.id);
    setReminderErrors(prev => { const next = { ...prev }; delete next[item.id]; return next; });

    try {
      // Permission check: verify the event exists and the current user is a participant.
      //
      // TOCTOU note: there is a theoretical race between this read and the updateDoc below
      // — the event could be deleted or the user removed between the two calls.
      // This is acceptable because:
      //   1. Firestore security rules are the authoritative enforcement layer.
      //   2. The window between read and write is milliseconds.
      //   3. The consequence (stale write fails server-side) is benign.
      // This client check is defence-in-depth for fast, user-friendly feedback only.
      const eventSnap = await getDoc(doc(db, 'calendarEvents', eventId));
      if (!eventSnap.exists()) {
        setReminderErrors(prev => ({ ...prev, [item.id]: 'Afspraak niet meer gevonden.' }));
        return;
      }
      const eventData = eventSnap.data() as any;
      const currentUid = userProfile?.uid;
      if (currentUid && Array.isArray(eventData?.participants) && !eventData.participants.includes(currentUid)) {
        console.warn('[InboxPage] saveReminderSetting: user is not a participant of event', eventId.slice(0, 8));
        setReminderErrors(prev => ({ ...prev, [item.id]: 'Geen rechten om deze herinnering aan te passen.' }));
        return;
      }

      const newReminderAt = minutesBefore > 0
        ? new Date(startTime.getTime() - minutesBefore * 60 * 1000)
        : null;
      await updateDoc(doc(db, 'calendarEvents', eventId), {
        reminderEnabled: minutesBefore > 0,
        reminderMinutesBefore: minutesBefore,
        reminderAt: newReminderAt,
        reminderSent: false, // reset so GlobalAgendaNotification fires again
      });
      setReminderSettings(prev => ({ ...prev, [item.id]: minutesBefore }));
    } catch (err) {
      console.error('[InboxPage] saveReminderSetting error:', err);
      setReminderErrors(prev => ({ ...prev, [item.id]: 'Kon herinnering niet opslaan. Probeer het opnieuw.' }));
    } finally {
      setSavingReminder(null);
    }
  };

  const handleAction = (item: InboxItem) => {
    if (!item.actionData) return;

    switch (item.actionData.actionType) {
      case 'join_meeting':
        if (item.actionData.actionUrl) {
          window.open(item.actionData.actionUrl, '_blank');
        }
        break;
      case 'accept_connection':
        if (item.actionData.connectionId) {
          // Use navigation helper - integrates with AI Assistant
          openConnectieFlow(item.actionData.connectionId);
        } else {
          alert(' ConnectionId ontbreekt voor deze reminder.');
        }
        break;
      case 'view_summary':
        setSelectedItem(item);
        setShowItemModal(true);
        break;
      case 'accept_invitation':
        // Navigate to chat or connections - integrates with AI Assistant
        openChat();
        break;
      case 'view_call':
        // Navigate to agenda or show call details - integrates with AI Assistant
        openAgenda();
        break;
    }

    // Mark as read when action is taken
    if (!item.isRead) {
      markAsRead(item.id);
    }
  };

  const acceptProposalSlot = async (inboxItem: InboxItem, slot: { start: string; end: string }) => {
    if (!userProfile?.uid) return;

    const proposalId = inboxItem.actionData?.data?.proposalId || (inboxItem as any).data?.proposalId;
    const connectionId = inboxItem.actionData?.connectionId || (inboxItem as any).data?.connectionId;
    const fromUserId = inboxItem.actionData?.otherUserId || (inboxItem as any).fromUserId;
    const fromUserName = inboxItem.actionData?.senderName || (inboxItem as any).fromUserName || 'Connectie';

    if (!proposalId || !connectionId || !fromUserId) {
      alert(' Voorstel gegevens ontbreken');
      return;
    }

    // Prevent duplicate calls
    if (processingProposal === inboxItem.id) {
      console.warn('Already processing this proposal, ignoring duplicate call');
      return;
    }

    setProcessingProposal(inboxItem.id);

    try {
      const rangeText = formatRangeNl(new Date(slot.start), new Date(slot.end));
      const currentUserName = userProfile.displayName || 'Connectie';

      // 1. Update meetingProposals status
      await updateDoc(doc(db, 'meetingProposals', proposalId), {
        status: 'accepted',
        acceptedSlot: slot,
        acceptedAt: serverTimestamp(),
      });

      // Check if event already exists (prevent duplicates)
      const existingEventsQuery = query(
        collection(db, 'calendarEvents'),
        where('participants', 'array-contains', userProfile.uid),
        where('type', '==', 'networking')
      );
      const existingSnapshot = await getDocs(existingEventsQuery);
      const slotStart = new Date(slot.start);
      const slotEnd = new Date(slot.end);
      const isDuplicate = existingSnapshot.docs.some(doc => {
        const data = doc.data();
        const docStart = data.startTime?.toDate?.() || new Date(data.startTime);
        const docEnd = data.endTime?.toDate?.() || new Date(data.endTime);
        return (
          docStart.getTime() === slotStart.getTime() &&
          docEnd.getTime() === slotEnd.getTime() &&
          data.participants?.includes(fromUserId)
        );
      });
      
      if (isDuplicate) {
        alert('✓ Deze afspraak is al gepland');
        setProcessingProposal(null);
        await loadInboxData();
        return;
      }
      
      // 2. Create calendar event (title is generic, display logic uses participantNames)
      const eventDoc = await addDoc(collection(db, 'calendarEvents'), {
        title: 'Netwerken',
        description: 'Gepland via voorstel',
        startTime: Timestamp.fromDate(new Date(slot.start)),
        endTime: Timestamp.fromDate(new Date(slot.end)),
        type: 'networking',
        source: 'free_for_networking',
        eventType: 'networking_filled',
        categoryColor: 'purple',
        color: '#C084FC',
        participants: [fromUserId, userProfile.uid],
        participantNames: {
          [fromUserId]: fromUserName,
          [userProfile.uid]: currentUserName,
        },
        connectionId: connectionId,
        createdBy: userProfile.uid,
        createdAt: serverTimestamp(),
        status: 'scheduled',
      });

      // 3. Update inbox item (using dot notation to avoid undefined fields)
      await updateDoc(doc(db, 'inboxItems', inboxItem.id), {
        isRead: true,
        title: `Afspraak gepland met ${fromUserName}`,
        message: `Gekozen moment: ${rangeText}`,
        subtitle: rangeText,
        'metadata.status': 'handled',
        'metadata.handledAt': serverTimestamp(),
        'metadata.acceptedSlot': slot,
        'metadata.eventId': eventDoc.id,
      });

      // 4. Inbox items for both users via central helper
      const itemPhase = (inboxItem as any).data?.phase || (inboxItem as any).phase || 'physical';
      const validPhases = ['chat', 'call', 'video', 'physical'] as const;
      const resolvedPhase = validPhases.includes(itemPhase) ? itemPhase : 'physical';
      await createAppointmentInboxItems({
        creatorUid: fromUserId,
        creatorDisplayName: fromUserName,
        otherUserId: userProfile.uid,
        otherUserName: currentUserName,
        connectionId,
        eventId: eventDoc.id,
        start: slot.start,
        end: slot.end,
        dateRangeText: rangeText,
        phase: resolvedPhase,
        locationName: (inboxItem as any).data?.location ?? null,
      });

      // 5. Notification to proposal sender (best-effort)
      await addDoc(collection(db, 'notifications'), {
        userId: fromUserId,
        type: 'meeting_confirmed',
        title: 'Voorstel geaccepteerd',
        message: `${currentUserName} accepteerde: ${rangeText}`,
        data: { connectionId, eventId: eventDoc.id, start: slot.start, end: slot.end },
        read: false,
        createdAt: serverTimestamp(),
      }).catch(err => console.warn('[InboxPage] acceptProposalSlot notification failed:', err));

      // Refresh inbox
      await loadInboxData();
      alert('Afspraak geaccepteerd!');
    } catch (error) {
      console.error('Error accepting proposal:', error);
      alert('Fout bij accepteren voorstel');
    } finally {
      setProcessingProposal(null);
    }
  };

  const declineProposal = async (inboxItem: InboxItem) => {
    if (!userProfile?.uid) return;

    const proposalId = inboxItem.actionData?.data?.proposalId || (inboxItem as any).data?.proposalId;
    const connectionId = inboxItem.actionData?.connectionId || (inboxItem as any).data?.connectionId;
    const fromUserId = inboxItem.actionData?.otherUserId || (inboxItem as any).fromUserId;
    const fromUserName = inboxItem.actionData?.senderName || (inboxItem as any).fromUserName || 'Connectie';

    if (!proposalId || !fromUserId) {
      alert(' Voorstel gegevens ontbreken');
      return;
    }

    setProcessingProposal(inboxItem.id);

    try {
      const currentUserName = userProfile.displayName || 'Connectie';

      // 1. Update meetingProposals status
      await updateDoc(doc(db, 'meetingProposals', proposalId), {
        status: 'declined',
        declinedAt: serverTimestamp(),
      });

      // 2. Update inbox item (using dot notation to avoid undefined fields)
      await updateDoc(doc(db, 'inboxItems', inboxItem.id), {
        isRead: true,
        title: ` Voorstel afgewezen`,
        message: `Je hebt het voorstel van ${fromUserName} afgewezen.`,
        subtitle: 'Afgewezen',
        'metadata.status': 'handled',
        'metadata.handledAt': serverTimestamp(),
      });

      // 3. Notify user1 (proposal sender)
      await addDoc(collection(db, 'notifications'), {
        userId: fromUserId,
        type: 'proposal_declined',
        title: ' Voorstel geweigerd',
        message: `${currentUserName} heeft je voorstel geweigerd. Stuur nieuwe momenten.`,
        data: {
          connectionId,
          proposalId,
        },
        read: false,
        createdAt: serverTimestamp(),
      });

      // Refresh inbox
      await loadInboxData();
      alert('Voorstel geweigerd');
    } catch (error) {
      console.error('Error declining proposal:', error);
      alert(' Fout bij weigeren voorstel');
    } finally {
      setProcessingProposal(null);
    }
  };

  // ─── Accept a proposal_received or counter_received slot ───────────────────
  const handleAcceptNewProposal = async (inboxItem: InboxItem, slot: { start: string; end: string }) => {
    if (!userProfile?.uid) return;
    if (processingNewProposal === inboxItem.id) return;

    const connectionId = (inboxItem as any).data?.connectionId;
    const proposalId = (inboxItem as any).data?.proposalId;
    const fromUserId = (inboxItem as any).fromUserId;
    const fromUserName = (inboxItem as any).fromUserName || 'Connectie';

    if (!connectionId) { alert('Verbindingsgegevens ontbreken'); return; }

    setProcessingNewProposal(inboxItem.id);
    try {
      const slotStart = new Date(slot.start);
      const slotEnd = new Date(slot.end);
      const currentUserName = userProfile.displayName || 'Connectie';

      // 1. Create calendarEvent for both users
      const eventDoc = await addDoc(collection(db, 'calendarEvents'), {
        title: 'Netwerkafspraak',
        description: 'Gepland via voorstel',
        startTime: Timestamp.fromDate(slotStart),
        endTime: Timestamp.fromDate(slotEnd),
        type: 'networking',
        source: 'proposal_flow',
        participants: [fromUserId, userProfile.uid].filter(Boolean),
        participantNames: {
          ...(fromUserId ? { [fromUserId]: fromUserName } : {}),
          [userProfile.uid]: currentUserName,
        },
        connectionId,
        createdBy: userProfile.uid,
        createdAt: serverTimestamp(),
        status: 'scheduled',
      });

      // 2. Update proposal subcollection (if proposalId exists)
      if (proposalId && connectionId) {
        try {
          const { updateDoc: fUpdate, doc: fDoc } = await import('firebase/firestore');
          await fUpdate(fDoc(db, 'connections', connectionId, 'proposals', proposalId), {
            status: 'accepted',
            acceptedSlot: slot,
            updatedAt: serverTimestamp(),
          });
        } catch (_) {}
      }

      // 3. Update physicalMeeting.status on connection
      await updateDoc(doc(db, 'connections', connectionId), {
        'physicalMeeting.status': 'scheduled',
        'physicalMeeting.appointmentStart': slot.start,
        'physicalMeeting.appointmentEnd': slot.end,
        appointmentConfirmed: true,
        appointmentStart: slot.start,
        appointmentEnd: slot.end,
      });

      // 4. Mark inbox item handled
      await updateDoc(doc(db, 'inboxItems', inboxItem.id), {
        isRead: true,
        'metadata.status': 'handled',
        'metadata.handledAt': serverTimestamp(),
        'metadata.acceptedSlot': slot,
        'metadata.eventId': eventDoc.id,
      });

      // 5. Inbox items for both users via central helper
      const rangeText = formatRangeNl(slotStart, slotEnd);
      const newProposalPhase = (inboxItem as any).data?.phase || (inboxItem as any).phase || 'physical';
      const validNewPhases = ['chat', 'call', 'video', 'physical'] as const;
      const resolvedNewPhase = validNewPhases.includes(newProposalPhase) ? newProposalPhase : 'physical';
      await createAppointmentInboxItems({
        creatorUid: fromUserId || '',
        creatorDisplayName: fromUserName,
        otherUserId: userProfile.uid,
        otherUserName: currentUserName,
        connectionId,
        eventId: eventDoc.id,
        start: slot.start,
        end: slot.end,
        dateRangeText: rangeText,
        phase: resolvedNewPhase,
        locationName: (inboxItem as any).data?.location ?? null,
      });

      await loadInboxData();
    } catch (err) {
      console.error('accept new proposal error', err);
      alert('Fout bij accepteren voorstel');
    } finally {
      setProcessingNewProposal(null);
    }
  };

  // ─── Send counter-proposal ──────────────────────────────────────────────────
  const handleSendCounter = async (inboxItem: InboxItem) => {
    if (!userProfile?.uid) return;
    if (sendingCounter) return;

    const connectionId = (inboxItem as any).data?.connectionId;
    const proposalId = (inboxItem as any).data?.proposalId;
    const fromUserId = (inboxItem as any).fromUserId;
    const fromUserName = (inboxItem as any).fromUserName || 'Connectie';

    if (!connectionId || !proposalId || !fromUserId) {
      alert('Gegevens ontbreken voor tegenbod');
      return;
    }

    // Validate counter slots
    const validSlots = counterSlotInputs
      .filter(s => s.date && s.startTime && s.endTime)
      .map(s => {
        const start = new Date(`${s.date}T${s.startTime}`);
        const end = new Date(`${s.date}T${s.endTime}`);
        return { startTime: start, endTime: end, start: start.toISOString(), end: end.toISOString() };
      })
      .filter(s => s.endTime > s.startTime);

    if (validSlots.length < 1) {
      alert('Voeg minimaal 1 geldig tijdsblok toe');
      return;
    }

    setSendingCounter(true);
    try {
      // 1. Update proposal subcollection with counter
      await serviceCounterProposal(
        connectionId,
        proposalId,
        validSlots.map(s => ({ startTime: s.startTime, endTime: s.endTime }))
      );

      // 2. Fetch current user phone to share
      let myPhone: string | null = null;
      try {
        const snap = await getDoc(doc(db, 'userProfiles', userProfile.uid));
        if (snap.exists()) { const d = snap.data(); myPhone = d.phone || d.phoneNumber || null; }
      } catch (_) {}

      // 3. Create counter_received inbox item for original sender
      await addDoc(collection(db, 'inboxItems'), {
        userId: fromUserId,
        type: 'counter_received',
        category: 'planning',
        title: `${userProfile.displayName || 'Connectie'} stuurde een tegenbod`,
        message: `${userProfile.displayName || 'Je connectie'} heeft ${validSlots.length} alternatief${validSlots.length === 1 ? '' : 'e'} tijdstip${validSlots.length === 1 ? '' : 'pen'} voorgesteld.`,
        isRead: false,
        priority: 'high',
        createdAt: serverTimestamp(),
        fromUserId: userProfile.uid,
        fromUserName: userProfile.displayName || 'Connectie',
        fromPhone: myPhone,
        data: {
          proposalId,
          connectionId,
          slots: validSlots.map(s => ({ start: s.start, end: s.end })),
        },
      });

      // 4. Mark original inbox item handled
      await updateDoc(doc(db, 'inboxItems', inboxItem.id), {
        isRead: true,
        'metadata.status': 'handled',
        'metadata.handledAt': serverTimestamp(),
      });

      setCounterModalItemId(null);
      setCounterSlotInputs([
        { date: '', startTime: '', endTime: '' },
        { date: '', startTime: '', endTime: '' },
        { date: '', startTime: '', endTime: '' },
      ]);
      await loadInboxData();
    } catch (err) {
      console.error('send counter proposal error', err);
      alert('Fout bij versturen tegenbod');
    } finally {
      setSendingCounter(false);
    }
  };

  const getFilteredItems = () => {
    const active = inboxItems.filter(item => !item.isArchived);
    const isGroupInvitationLike = (item: InboxItem) => {
      const actionType = (item as any)?.actionData?.actionType;
      const title = (item.title || '').toLowerCase();
      const message = (item.message || '').toLowerCase();
      return (
        item.type === 'group_invitation' ||
        actionType === 'group_invitation' ||
        title.includes('uitnodiging voor') ||
        title.includes('uitgenodigd voor') ||
        message.includes('toegevoegd als lid')
      );
    };

    const isConfirmationLike = (item: InboxItem) => item.type === 'confirmation' || isGroupInvitationLike(item);

    switch (activeTab) {
      case 'archived':
        return inboxItems.filter(item => item.isArchived);
      case 'unread':
        return active.filter(item => !item.isRead);
      case 'reminders':
        return active.filter(item => item.type === 'reminder');
      case 'summaries':
        return active.filter(item => item.type === 'summary');
      case 'confirmations':
        return active.filter(isConfirmationLike);
      // case 'chat': // Hidden - incomplete module
      //   return active.filter(item => item.type === 'chat');
      case 'connection_requests':
        return active.filter(item => item.type === 'connection_request');
      case 'podcast_invites':
        return active.filter(item => item.type === 'podcast_invite');
      case 'matchmaking':
        return active.filter(item => item.type === 'matchmaking');
      case 'partnerships':
        return active.filter(item => item.type === 'partnership');
      case 'planning':
        return active.filter(item =>
          item.category === 'planning' ||
          item.type === 'meeting_proposal' ||
          item.type === 'proposal_accepted' ||
          item.type === 'planning_pending' ||
          item.type === 'proposal_received' ||
          item.type === 'counter_received' ||
          (item.category === 'meeting' && item.title.includes('wijzigingsverzoek'))
        );
      case 'afspraken':
        return active.filter(item =>
          item.type === 'appointment_confirmed' ||
          item.category === 'afspraak'
        );
      case 'event_invitations':
        return active.filter(item => item.type === 'event_invitation');
      default:
        return active;
    }
  };

  const getItemIcon = (item: InboxItem): React.ReactNode => {
    const iconProps = { size: 18, className: "text-gray-500" };
    if (item.type === 'planning_pending') return <CalendarIcon size={18} className="text-amber-500" />;
    if (item.type === 'appointment_confirmed') return <CalendarIcon size={18} style={{ color: '#4F6660' }} />;
    switch (item.category) {
      case 'call': return <PhoneIcon {...iconProps} />;
      case 'meeting': return <PartnerIcon {...iconProps} />;
      case 'afspraak': return <CalendarIcon size={18} style={{ color: '#4F6660' }} />;
      case 'planning': return <CalendarIcon {...iconProps} />;
      case 'podcast': return <Mic {...iconProps} />;
      case 'connection': return <CommunityIcon {...iconProps} />;
      case 'achievement': return <Trophy {...iconProps} />;
      case 'system': return <SettingsIcon {...iconProps} />;
      case 'chat': return <ChatIcon {...iconProps} />;
      case 'matchmaking': return <Heart {...iconProps} />;
      case 'partnership': return <PartnerIcon {...iconProps} />;
      case 'social': return <Smartphone {...iconProps} />;
      default: return <MailIcon {...iconProps} />;
    }
  };

  const getPriorityColor = (priority: InboxItem['priority']) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-stone-100 text-[#4A5A3D] border-stone-200';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Nu';
    if (diffInMinutes < 60) return `${diffInMinutes} min geleden`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} uur geleden`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} dag${diffInDays > 1 ? 'en' : ''} geleden`;
    
    return date.toLocaleDateString('nl-NL');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const filteredItems = getFilteredItems();
  const activeInboxItems = inboxItems.filter(item => !item.isArchived);

  const isGroupInvitationLike = (item: InboxItem) => {
    const actionType = (item as any)?.actionData?.actionType;
    const title = (item.title || '').toLowerCase();
    const message = (item.message || '').toLowerCase();
    return (
      item.type === 'group_invitation' ||
      actionType === 'group_invitation' ||
      title.includes('uitnodiging voor') ||
      title.includes('uitgenodigd voor') ||
      message.includes('toegevoegd als lid')
    );
  };
  const isConfirmationLike = (item: InboxItem) => item.type === 'confirmation' || isGroupInvitationLike(item);

  // Helper: bepaal of een item in een van de vaste categorieën valt
  const isCategorizeable = (item: InboxItem) =>
    item.type === 'reminder' ||
    item.type === 'summary' ||
    item.type === 'appointment_confirmed' ||
    item.category === 'afspraak' ||
    item.category === 'planning' ||
    item.type === 'meeting_proposal' ||
    item.type === 'proposal_accepted' ||
    item.type === 'planning_pending' ||
    item.type === 'proposal_received' ||
    item.type === 'counter_received' ||
    (item.category === 'meeting' && (item.title || '').includes('wijzigingsverzoek')) ||
    isConfirmationLike(item) ||
    item.type === 'connection_request' ||
    item.type === 'podcast_invite' ||
    item.type === 'event_invitation' ||
    item.type === 'matchmaking' ||
    item.type === 'partnership';

  // Overige berichten: items die in geen enkele categorie vallen
  const uncategorizedItems = activeInboxItems.filter(item => !isCategorizeable(item));

  // Ongelezen = alleen items die zichtbaar zijn in een categorie of "Overige berichten"
  // Dit voorkomt dat "phantom" items de teller ophogen
  const unreadCount = activeInboxItems.filter(item => !item.isRead).length;

  return (
    <div className="quiet-lounge inbox-mobile-bg min-h-full bg-[#edeae4] p-0 md:min-h-screen md:bg-[#F4F4F1] md:p-6">
      <div className="grid grid-cols-12 gap-0 md:gap-8 max-w-5xl mx-auto">
        {/* Left Sidebar - Individual Cards */}
        <div className="hidden lg:block lg:col-span-3">
          <div className="flex flex-col gap-4">
            {/* Stats Overview Card */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center space-x-3 mb-4">
                <AnalyticsIcon size={18} weight="regular" className="text-gray-500" />
                <div>
                  <h3 className="font-medium">Overzicht</h3>
                  <p className="text-sm text-gray-500">Inbox statistieken</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{activeInboxItems.length}</p>
                  <p className="text-xs text-gray-600">Totaal</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{unreadCount}</p>
                  <p className="text-xs text-gray-600">Ongelezen</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {activeInboxItems.filter(item => item.type === 'summary').length}
                  </p>
                  <p className="text-xs text-gray-600">Samenvattingen</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#4F6660]">
                    {activeInboxItems.filter(item => item.type === 'reminder').length}
                  </p>
                  <p className="text-xs text-gray-600">Reminders</p>
                </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600">
                {activeInboxItems.filter(item =>
                  item.type === 'appointment_confirmed' ||
                  item.category === 'afspraak'
                ).length}
              </p>
              <p className="text-xs text-gray-600">Afspraken</p>
            </div>
              </div>
            </div>

            {/* Filter Categories Card */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <div className="flex items-center space-x-2">
                  <Search size={18} className="text-gray-500" />
                  <div>
                    <h3 className="font-medium">Filters</h3>
                    <p className="text-sm text-gray-500">Categorieën en types</p>
                  </div>
                </div>
              </div>
              <div className="p-3 space-y-1">
                <button
                  onClick={() => setActiveTab('reminders')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'reminders' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <NotificationIcon size={14} className="text-gray-400" /> Reminders
                      {activeInboxItems.filter(item => item.type === 'reminder' && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item => item.type === 'reminder').length})
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('afspraken')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'afspraken' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <CalendarIcon size={14} className="text-gray-400" /> Afspraken
                      {activeInboxItems.filter(item => (
                        item.type === 'appointment_confirmed' ||
                        item.category === 'afspraak'
                      ) && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item =>
                        item.type === 'appointment_confirmed' ||
                        item.category === 'afspraak'
                      ).length})
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('planning')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'planning' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <CalendarIcon size={14} className="text-gray-400" /> Planning
                      {activeInboxItems.filter(item => (
                        item.category === 'planning' ||
                        item.type === 'meeting_proposal' ||
                        item.type === 'proposal_accepted' ||
                        item.type === 'planning_pending' ||
                        (item.category === 'meeting' && item.title.includes('wijzigingsverzoek'))
                      ) && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item =>
                        item.category === 'planning' ||
                        item.type === 'meeting_proposal' ||
                        item.type === 'proposal_accepted' ||
                        item.type === 'planning_pending' ||
                        (item.category === 'meeting' && item.title.includes('wijzigingsverzoek'))
                      ).length})
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('summaries')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'summaries' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <DocumentIcon size={14} className="text-gray-400" /> Samenvattingen
                      {activeInboxItems.filter(item => item.type === 'summary' && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item => item.type === 'summary').length})
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('samenvattingen')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'samenvattingen' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <AnalyticsIcon size={14} className="text-gray-400" /> AI Analyses
                    </span>
                    <span className="text-xs text-gray-500">
                      ({analyses.length})
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('confirmations')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'confirmations' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <SuccessIcon size={14} className="text-gray-400" /> Bevestigingen
                      {activeInboxItems.filter(item => isConfirmationLike(item) && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(isConfirmationLike).length})
                    </span>
                  </div>
                </button>
                {/* Hidden incomplete module (not deleted, just hidden from navigation):
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'chat' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5"><ChatIcon size={14} className="text-gray-400" /> Chat</span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item => item.type === 'chat').length})
                    </span>
                  </div>
                </button>
                */}
                <button
                  onClick={() => setActiveTab('connection_requests')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'connection_requests' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <PartnerIcon size={14} className="text-gray-400" /> Connecties
                      {activeInboxItems.filter(item => item.type === 'connection_request' && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item => item.type === 'connection_request').length})
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('podcast_invites')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'podcast_invites' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <Mic size={14} className="text-gray-400" /> Podcasts
                      {activeInboxItems.filter(item => item.type === 'podcast_invite' && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item => item.type === 'podcast_invite').length})
                    </span>
                  </div>
                </button>
                {/* Evenement Uitnodigingen tab */}
                <button
                  onClick={() => setActiveTab('event_invitations')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'event_invitations' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <CalendarIcon size={14} className="text-gray-400" /> Uitnodigingen
                      {activeInboxItems.filter(item => item.type === 'event_invitation' && !item.isRead).length > 0 && (
                        <span className="h-2 w-2 rounded-full bg-purple-500 inline-block" />
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({activeInboxItems.filter(item => item.type === 'event_invitation').length})
                    </span>
                  </div>
                </button>
                {/* Scheidingslijn vóór archief */}
                {inboxItems.filter(item => item.isArchived).length > 0 && (
                  <div className="border-t border-gray-100 my-1" />
                )}
                {/* Overige berichten — items die in geen categorie vallen */}
                {uncategorizedItems.length > 0 && (
                  <button
                    onClick={() => setActiveTab('all')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                      activeTab === 'all' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium flex items-center gap-1.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                        Overige berichten
                        {uncategorizedItems.filter(i => !i.isRead).length > 0 && (
                          <span className="h-2 w-2 rounded-full bg-blue-500 inline-block" />
                        )}
                      </span>
                      <span className="text-xs text-gray-500">({uncategorizedItems.length})</span>
                    </div>
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('archived')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    activeTab === 'archived'
                      ? 'border'
                      : 'hover:bg-red-50 border border-transparent'
                  }`}
                  style={activeTab === 'archived' ? { background: '#fdf0f0', color: '#8B3E3E', borderColor: '#e0c4c4' } : undefined}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-1.5" style={activeTab === 'archived' ? { color: '#8B3E3E' } : { color: '#6b7280' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                      Archief
                    </span>
                    <span className="text-xs text-gray-500">
                      ({inboxItems.filter(item => item.isArchived).length})
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="col-span-12 lg:col-span-9 space-y-0 md:space-y-6">
          {/* Header */}
          <div className="hidden md:block">
            <PageHeader 
              title="Inbox"
              subtitle="Notificaties, samenvattingen en reminders"
              icon={<MailIcon size={24} className="text-gray-500" />}
            />
          </div>

          {/* Inbox Content */}
          <div className="bg-[#F4F1EA] md:bg-white md:rounded-lg md:shadow-sm md:border">
              <div className="px-5 py-4 md:p-4 border-b border-[#E8E4DC] md:border-b md:border-gray-100 bg-[#F4F1EA] md:bg-white">
                <h2 className="text-base md:text-lg font-semibold text-gray-900">
                  {activeTab === 'all' ? 'Inbox' :
                   activeTab === 'reminders' ? 'Reminders' :
                   activeTab === 'summaries' ? 'Samenvattingen' :
                   activeTab === 'samenvattingen' ? 'AI Analyses' :
                   activeTab === 'confirmations' ? 'Notificaties' :
                   activeTab === 'connection_requests' ? 'Connectie Verzoeken' :
                   activeTab === 'podcast_invites' ? 'Podcast Uitnodigingen' :
                   activeTab === 'afspraken' ? 'Afspraken' :
                   activeTab === 'planning' ? 'Planning' :
                   activeTab === 'archived' ? 'Archief' :
                   activeTab === 'event_invitations' ? 'Evenement Uitnodigingen' :
                   'Notificaties'}
                </h2>
                <p className="text-xs md:text-sm text-gray-500 md:text-gray-600">
                  {activeTab === 'samenvattingen' ? `${analyses.length} analyse${analyses.length !== 1 ? 's' : ''} gevonden` : `${filteredItems.length} item${filteredItems.length !== 1 ? 's' : ''} gevonden`}
                </p>
              </div>

              {/* Mobile inbox onderdelen — compact, doorlopend, geen losse kaarten */}
              <div className="md:hidden border-b border-[#E8E4DC] bg-[#F4F1EA] px-4 py-3">
                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {([
                    { key: 'all' as const, label: 'Alles', count: activeInboxItems.length },
                    { key: 'reminders' as const, label: 'Reminders', count: activeInboxItems.filter(item => item.type === 'reminder').length },
                    { key: 'afspraken' as const, label: 'Afspraken', count: activeInboxItems.filter(item => item.type === 'appointment_confirmed' || item.category === 'afspraak').length },
                    { key: 'planning' as const, label: 'Planning', count: activeInboxItems.filter(item =>
                      item.category === 'planning' ||
                      item.type === 'meeting_proposal' ||
                      item.type === 'proposal_accepted' ||
                      item.type === 'planning_pending' ||
                      item.type === 'proposal_received' ||
                      item.type === 'counter_received' ||
                      (item.category === 'meeting' && item.title.includes('wijzigingsverzoek'))
                    ).length },
                    { key: 'summaries' as const, label: 'Samenvattingen', count: activeInboxItems.filter(item => item.type === 'summary').length },
                    { key: 'samenvattingen' as const, label: 'AI Analyses', count: analyses.length },
                    { key: 'confirmations' as const, label: 'Notificaties', count: activeInboxItems.filter(isConfirmationLike).length },
                    { key: 'connection_requests' as const, label: 'Connecties', count: activeInboxItems.filter(item => item.type === 'connection_request').length },
                    { key: 'podcast_invites' as const, label: 'Podcasts', count: activeInboxItems.filter(item => item.type === 'podcast_invite').length },
                    { key: 'event_invitations' as const, label: 'Uitnodigingen', count: activeInboxItems.filter(item => item.type === 'event_invitation').length },
                    { key: 'archived' as const, label: 'Archief', count: inboxItems.filter(item => item.isArchived).length },
                  ]).map(({ key, label, count }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveTab(key)}
                      className={`flex flex-shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                        activeTab === key
                          ? 'border-[#8DA8A0] bg-[#F0F7F4] text-[#4F6660]'
                          : 'border-gray-200 bg-white/45 text-gray-500'
                      }`}
                    >
                      <span>{label}</span>
                      <span className="text-[10px] opacity-70">({count})</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* AI Analyses Tab - NIEUW */}
              {activeTab === 'samenvattingen' ? (
                <div className="p-6 bg-[#F4F1EA] md:bg-transparent">
                  {loadingAnalyses ? (
                    <div className="text-center py-8 text-gray-500">
                      Laden...
                    </div>
                  ) : analyses.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="mb-4"><AnalyticsIcon size={48} className="text-gray-300 mx-auto" /></div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Nog geen analyses</h3>
                      <p className="text-gray-600 mb-4">
                        Complete een gesprek en vraag een AI analyse aan (5 credits)
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {analyses.map(analysis => (
                        <ConversationAnalysisItem 
                          key={analysis.id}
                          analysis={analysis}
                          onRead={() => loadAnalyses()}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : activeTab === 'event_invitations' ? (
                /* ── Event Invitations Tab ───────────────────── */
                <div className="p-6 bg-[#F4F1EA] md:bg-transparent">
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="text-4xl mb-3">📅</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Geen evenementuitnodigingen</h3>
                      <p className="text-sm text-gray-500">Uitnodigingen voor groepsevents verschijnen hier.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredItems.map((item) => {
                        const ed = (item as any).eventData || {};
                        const dateStr = ed.date ? new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(ed.date)) : '';
                        const rsvp = (item as any).rsvpResponse as 'yes' | 'no' | 'maybe' | null;
                        const rsvpEmoji = rsvp === 'yes' ? '✅' : rsvp === 'no' ? '❌' : rsvp === 'maybe' ? '🤔' : null;

                        const handleInboxRSVP = async (response: 'yes' | 'no' | 'maybe') => {
                          try {
                            const { updateDoc: ud, doc: fd, setDoc: sd, serverTimestamp: st, increment: inc } = await import('firebase/firestore');
                            await ud(fd(db, 'inboxItems', item.id), { rsvpResponse: response, isRead: true, respondedAt: st() });
                            if ((item as any).eventId && userProfile?.uid) {
                              await sd(fd(db, 'eventRSVPs', `${(item as any).eventId}_${userProfile.uid}`), {
                                eventId: (item as any).eventId, userId: userProfile.uid, response, respondedAt: st(),
                              }, { merge: true });
                              const field = response === 'yes' ? 'rsvpYes' : response === 'no' ? 'rsvpNo' : 'rsvpMaybe';
                              try { await ud(fd(db, 'clubEvents', (item as any).eventId), { [field]: inc(1) }); } catch {}
                            }
                            setInboxItems(prev => prev.map(i => i.id === item.id ? { ...i, rsvpResponse: response, isRead: true } as any : i));
                          } catch (e) { console.error('Inbox RSVP failed:', e); }
                        };

                        return (
                          <div key={item.id} className={`rounded-xl border overflow-hidden transition-all ${!item.isRead ? 'border-purple-200 bg-purple-50/30' : 'border-gray-200 bg-white'}`}>
                            {/* Cover */}
                            {ed.coverImageUrl && (
                              <img src={ed.coverImageUrl} alt={ed.title} className="w-full h-28 object-cover" />
                            )}
                            <div className="p-4 space-y-2">
                              {(item as any).clubName && (
                                <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#7A9488' }}>{(item as any).clubName}</p>
                              )}
                              <h3 className="font-bold text-gray-900">{ed.title || item.title}</h3>
                              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                {dateStr && <span>📅 {dateStr}{ed.startTime ? ` · ${ed.startTime}` : ''}</span>}
                                {ed.locationAddress && <span>📍 {ed.locationAddress}</span>}
                                {ed.onlineLink && <span>💻 Online</span>}
                                {ed.createdByName && <span>👤 {ed.createdByName}</span>}
                              </div>
                              {ed.description && (
                                <p className="text-xs text-gray-600 line-clamp-2">{ed.description}</p>
                              )}
                              {/* RSVP */}
                              {rsvpEmoji ? (
                                <div className="flex items-center gap-2 pt-1">
                                  <span className="text-lg">{rsvpEmoji}</span>
                                  <span className="text-xs text-gray-600 font-medium">
                                    {rsvp === 'yes' ? 'Aanwezig' : rsvp === 'no' ? 'Niet aanwezig' : 'Misschien aanwezig'}
                                  </span>
                                  <button
                                    onClick={() => setInboxItems(prev => prev.map(i => i.id === item.id ? { ...i, rsvpResponse: null } as any : i))}
                                    className="ml-auto text-xs text-gray-400 hover:text-gray-600 underline"
                                  >
                                    Wijzigen
                                  </button>
                                </div>
                              ) : (
                                <div className="flex gap-2 pt-1">
                                  {(['yes', 'maybe', 'no'] as const).map((r) => (
                                    <button
                                      key={r}
                                      onClick={() => handleInboxRSVP(r)}
                                      className="flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all hover:opacity-80"
                                      style={r === 'yes' ? { borderColor: '#16a34a', color: '#16a34a', backgroundColor: '#f0fdf4' } : r === 'no' ? { borderColor: '#dc2626', color: '#dc2626', backgroundColor: '#fef2f2' } : { borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
                                    >
                                      {r === 'yes' ? '✅ Ja' : r === 'no' ? '❌ Nee' : '🤔 Misschien'}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="min-h-[calc(100dvh-220px)] px-5 py-6 md:min-h-0 md:p-6">
                  {filteredItems.length === 0 ? (
                  <div className="flex min-h-[52vh] flex-col items-center justify-start px-6 pt-24 text-center">
                    <MailIcon size={34} className="mb-5 text-[#4F6660]/35" />
                    <h3 className="text-lg font-semibold leading-tight text-[#2F3A36]">Geen items gevonden</h3>
                    <p className="mt-2 max-w-[260px] text-sm leading-relaxed text-[#6F746F]">
                      {activeTab === 'all' 
                        ? 'Je inbox is leeg' 
                        : `Geen ${activeTab} items gevonden`
                      }
                    </p>
                  </div>
                ) : (
                  <>
                  {/* Pulse animation for appointment_confirmed cards — injected once here,
                      not inside the map loop, to prevent duplicate @keyframes injection. */}
                  <style>{`
                    @keyframes sk-appt-pulse {
                      0%   { box-shadow: 0 2px 12px rgba(79,102,96,0.15); }
                      50%  { box-shadow: 0 0 0 6px rgba(79,102,96,0.22); }
                      100% { box-shadow: 0 2px 12px rgba(79,102,96,0.15); }
                    }
                    .sk-appt-pulse {
                      animation: sk-appt-pulse 1.2s ease-in-out 3;
                    }
                  `}</style>
                  <div className="space-y-2">
                    {filteredItems.map(item => (
                      <div
                        key={item.id}
                        className={`group rounded-lg border transition-all duration-150 hover:shadow-sm cursor-pointer ${
                          !item.isRead 
                            ? 'bg-blue-50/50 border-blue-200/50 hover:bg-blue-50 hover:border-blue-300' 
                            : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start gap-4 px-5 py-4">
                          {/* Icon/Avatar - Fixed width for alignment */}
                          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                            {getItemIcon(item)}
                          </div>
                          
                          {/* Content - Main area */}
                          <div className="flex-1 min-w-0">
                            {/* Title row - Name + subtle metadata */}
                            <div className="flex items-start justify-between gap-3 mb-1">
                              <h3 className={`font-semibold text-[15px] leading-snug ${
                                !item.isRead ? 'text-gray-900' : 'text-gray-700'
                              }`}>
                                {item.title}
                              </h3>
                              {/* Time - always visible, subtle */}
                              <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">
                                {getTimeAgo(item.createdAt)}
                              </span>
                            </div>
                            
                            {/* Message preview */}
                            <p className="text-sm text-gray-600 leading-relaxed mb-2 line-clamp-2">
                              {item.message}
                            </p>
                            
                            {/* Metadata row - Badges, tags, event date */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Priority badge - subtle */}
                              {item.priority !== 'low' && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${getPriorityColor(item.priority)}`}>
                                  {item.priority === 'urgent' ? 'Urgent' : item.priority === 'high' ? 'Belangrijk' : 'Normaal'}
                                </span>
                              )}
                              
                              {/* Unread indicator */}
                              {!item.isRead && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                  Nieuw
                                </span>
                              )}
                              
                              {/* Recurring tag */}
                              {item.type === 'reminder' && item.metadata?.isRecurring && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-600 border border-purple-200">
                                  Terugkerend
                                </span>
                              )}
                              
                              {/* Event date */}
                              {item.metadata?.eventDate && (
                                <span className="inline-flex items-center text-xs text-gray-500">
                                  {item.metadata.eventDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>

                            {/* Show subtitle for planning items (only if not handled) - but NOT for meeting_proposal since slots are shown in expandable section */}
                            {item.category === 'planning' && item.subtitle && item.metadata?.status !== 'handled' && item.type !== 'meeting_proposal' && (
                              <p className="text-xs text-gray-600 mt-2 font-medium">
                                {item.subtitle}
                              </p>
                            )}
                            
                            {/* Connection name */}
                            {item.category === 'connection' && item.actionData?.connectionName && (
                              <p className="text-xs text-gray-500 mt-1">
                                Connectie: {item.actionData.connectionName}
                              </p>
                            )}
                            
                            {/* Meeting Proposal Slots */}
                            {item.type === 'meeting_proposal' && (item as any).data?.slots && !item.metadata?.status && (
                              <div className="mt-3 space-y-2">
                                <button
                                  onClick={() => setExpandedProposal(expandedProposal === item.id ? null : item.id)}
                                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                                    expandedProposal === item.id
                                      ? 'bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200'
                                      : 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
                                  }`}
                                >
                                  <span className="text-xs">{expandedProposal === item.id ? '▲' : '📅'}</span>
                                  {expandedProposal === item.id ? 'Verberg momenten' : `Bekijk ${((item as any).data.slots as Array<any>).length} voorgestelde momenten`}
                                </button>
                                {expandedProposal === item.id && (
                                  <div className="space-y-2 pl-3 border-l-2 border-blue-200">
                                    {((item as any).data.slots as Array<{ start: string; end: string }>).map((slot, idx) => (
                                      <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                                        <span className="text-sm text-gray-700">
                                          {formatRangeNl(new Date(slot.start), new Date(slot.end))}
                                        </span>
                                        <button
                                          onClick={() => acceptProposalSlot(item, slot)}
                                          disabled={processingProposal === item.id}
                                          className="bg-green-500 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {processingProposal === item.id ? 'Bezig...' : 'Kies dit moment'}
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      onClick={() => declineProposal(item)}
                                      disabled={processingProposal === item.id}
                                      className="w-full bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                                    >
                                      {processingProposal === item.id ? 'Bezig...' : 'Weiger voorstel'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Planning Pending — "Rond je planning af" kaart */}
                            {item.type === 'planning_pending' && (
                              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <CalendarIcon size={14} className="text-amber-600 flex-shrink-0" />
                                  <span className="text-xs font-semibold text-amber-800">
                                    Openstaande planning
                                  </span>
                                </div>
                                <p className="text-xs text-amber-700 mb-3">
                                  {(item as any).data?.otherUserName
                                    ? `Je was bezig met plannen met ${(item as any).data.otherUserName}. Ga verder waar je gebleven was.`
                                    : 'Je had een planning gestart. Ga verder waar je gebleven was.'}
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const cId = (item as any).data?.connectionId || item.actionData?.connectionId;
                                      if (cId) {
                                        openConnectieFlow(cId, 'agenda');
                                      }
                                      markAsRead(item.id);
                                    }}
                                    className="flex-1 bg-amber-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-amber-600 transition-colors"
                                  >
                                    📅 Ga nu plannen
                                  </button>
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      markAsRead(item.id);
                                    }}
                                    className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-500 hover:bg-gray-100 border border-gray-200 transition-colors"
                                  >
                                    Later
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* ── Afspraak bevestigd (appointment_confirmed) ── */}
                            {item.type === 'appointment_confirmed' && (() => {
                              const isPast = item.data?.end ? new Date(item.data.end) < new Date() : false;
                              const accent     = isPast ? '#8B3E3E' : '#4F6660';
                              const accentHover= isPast ? '#6b2e2e' : '#3a4e49';
                              const gradFrom   = isPast ? '#f5eeee' : '#eef3f1';
                              const gradTo     = isPast ? '#ede0df' : '#dde8e4';
                              const borderClr  = isPast ? '#b05a5a' : '#4F6660';
                              const rowBorder  = isPast ? '#e0c4c4' : '#c8d8d3';
                              const badgeBg    = isPast ? '#e8caca' : '#c8d8d3';
                              const badgeTxt   = isPast ? '#5a2020' : '#2c4a44';
                              const titleClr   = isPast ? '#2f1a1a' : '#1a2f2a';
                              const participantBg = isPast ? '#f5eeee' : '#eef3f1';
                              return (
                              <>
                              <div className="mt-3 rounded-xl p-3 space-y-2" style={{
                                background: `linear-gradient(135deg, ${gradFrom} 0%, ${gradTo} 100%)`,
                                border: `2px solid ${borderClr}`,
                                boxShadow: isPast ? '0 2px 12px rgba(139,62,62,0.12)' : '0 2px 12px rgba(79,102,96,0.15)',
                              }}>
                                {/* Header */}
                                <div className="flex items-center gap-2">
                                  <div style={{
                                    width: '28px', height: '28px', borderRadius: '50%',
                                    background: accent, display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', flexShrink: 0,
                                  }}>
                                    <CalendarIcon size={14} color="white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <p className="text-xs font-bold" style={{ color: titleClr }}>{item.title}</p>
                                      {isPast && (
                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: '#c97a7a', color: '#fff' }}>
                                          Afgelopen
                                        </span>
                                      )}
                                    </div>
                                    {item.phase && (
                                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: badgeBg, color: badgeTxt }}>
                                        {item.phase}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Date/time */}
                                {item.data?.start && item.data?.end && (
                                  <div className="bg-white rounded-lg px-3 py-2 flex items-center gap-2" style={{ border: `1px solid ${rowBorder}` }}>
                                    <CalendarIcon size={13} className="flex-shrink-0" style={{ color: accent } as React.CSSProperties} />
                                    <span className="text-xs font-semibold text-gray-800">
                                      {new Date(item.data.start).toLocaleDateString('nl-NL', {
                                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                                      })}
                                      {' · '}
                                      {new Date(item.data.start).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                      {' – '}
                                      {new Date(item.data.end).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} uur
                                    </span>
                                  </div>
                                )}

                                {/* Location */}
                                {(item.data?.location || item.subtitle) && (
                                  <div className="bg-white rounded-lg px-3 py-2 flex items-center gap-2" style={{ border: `1px solid ${rowBorder}` }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth={2.5} className="flex-shrink-0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                    <span className="text-xs font-semibold text-gray-800 truncate">
                                      {item.data?.location || item.subtitle}
                                    </span>
                                  </div>
                                )}

                                {/* Participants */}
                                {(item.fromUserName || userProfile?.displayName) && (
                                  <div className="bg-white rounded-lg px-3 py-2 flex items-center gap-2" style={{ border: `1px solid ${rowBorder}` }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth={2.5} className="flex-shrink-0"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                    <div className="flex gap-1.5 flex-wrap">
                                      {[userProfile?.displayName || 'Jij', item.fromUserName].filter(Boolean).map((name, i) => (
                                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: participantBg, color: badgeTxt, border: `1px solid ${rowBorder}` }}>
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Reminder setting — only when linked calendarEvent is known and appointment is not past */}
                                {item.data?.eventId && !isPast && (
                                  <div className="bg-white rounded-lg px-3 py-2 flex items-center gap-2" style={{ border: `1px solid ${rowBorder}` }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth={2.5} className="flex-shrink-0"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                                    <span className="text-xs text-gray-700 flex-shrink-0">Herinnering:</span>
                                    <select
                                      className="text-xs font-semibold text-gray-800 bg-transparent border-none outline-none cursor-pointer flex-1"
                                      value={reminderSettings[item.id] ?? (item.data?.reminderMinutesBefore ?? 1440)}
                                      disabled={savingReminder === item.id}
                                      onChange={(e) => {
                                        const minutes = parseInt(e.target.value, 10);
                                        saveReminderSetting(item, minutes);
                                      }}
                                    >
                                      <option value={0}>Geen herinnering</option>
                                      <option value={30}>30 minuten van tevoren</option>
                                      <option value={60}>1 uur van tevoren</option>
                                      <option value={180}>3 uur van tevoren</option>
                                      <option value={1440}>1 dag van tevoren</option>
                                      <option value={2880}>2 dagen van tevoren</option>
                                      <option value={10080}>1 week van tevoren</option>
                                    </select>
                                    {savingReminder === item.id && (
                                      <span className="text-[10px] flex-shrink-0" style={{ color: accent }}>Opslaan…</span>
                                    )}
                                  </div>
                                )}
                                {reminderErrors[item.id] && (
                                  <p className="text-[10px] text-red-600 pl-1 mt-0.5">{reminderErrors[item.id]}</p>
                                )}

                                {/* Knoppen rij */}
                                <div className={`flex gap-2 ${isPast ? '' : ''}`}>
                                  {/* Navigeer naar ConnectieFlow — verborgen als afgelopen */}
                                  {!isPast && (item.data?.connectionId || item.actionData?.connectionId) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const cId = item.data?.connectionId || item.actionData?.connectionId;
                                        openConnectieFlow(cId);
                                        markAsRead(item.id);
                                      }}
                                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs transition-colors"
                                      style={{ background: accent, color: '#ffffff', fontWeight: 600 }}
                                      onMouseEnter={e => { e.currentTarget.style.background = accentHover; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = accent; }}
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                      Ga naar ConnectieFlow
                                    </button>
                                  )}
                                  {/* Archiveer knop — alleen bij afgelopen afspraken */}
                                  {isPast && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        archiveItem(item.id);
                                      }}
                                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-colors"
                                      style={{ background: '#8B3E3E', color: '#ffffff' }}
                                      onMouseEnter={e => { e.currentTarget.style.background = '#6b2e2e'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = '#8B3E3E'; }}
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                                      Archiveren
                                    </button>
                                  )}
                                </div>
                              </div>
                              </>
                              );
                            })()}

                            {/* ── Voorstel ontvangen (proposal_received) ── */}
                            {(item.type === 'proposal_received' || item.type === 'counter_received') && item.metadata?.status !== 'handled' && (
                              <div className={`mt-3 rounded-xl border p-3 space-y-2.5 ${
                                item.type === 'counter_received'
                                  ? 'bg-purple-50 border-purple-200'
                                  : 'bg-blue-50 border-blue-200'
                              }`}>
                                {/* Header row with avatar + name */}
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                                      item.type === 'counter_received' ? 'bg-purple-200 text-purple-800' : 'bg-blue-200 text-blue-800'
                                    }`}>
                                      {(item.fromUserName || 'C').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <span className="text-xs font-semibold text-gray-800">
                                        {item.fromUserName || 'Connectie'}
                                      </span>
                                      {(item as any).fromPhone && (
                                        <a
                                          href={`tel:${(item as any).fromPhone}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="flex items-center gap-1 text-[10px] text-blue-600 hover:underline mt-0.5"
                                        >
                                          <PhoneIcon size={10} />
                                          {(item as any).fromPhone}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                    item.type === 'counter_received'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {item.type === 'counter_received' ? 'Tegenbod' : 'Voorstel'}
                                  </span>
                                </div>

                                {/* Slot options */}
                                <div className="space-y-1.5">
                                  {((item as any).data?.slots as Array<{ start: string; end: string }> || []).map((slot, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-white/60 shadow-sm"
                                    >
                                      <span className="text-xs text-gray-800 font-medium">
                                        {formatRangeNl(new Date(slot.start), new Date(slot.end))}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAcceptNewProposal(item, slot);
                                        }}
                                        disabled={processingNewProposal === item.id}
                                        className="ml-2 px-2.5 py-1 bg-green-500 text-white rounded-lg text-[10px] font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 flex-shrink-0"
                                      >
                                        {processingNewProposal === item.id ? '…' : 'Accepteer'}
                                      </button>
                                    </div>
                                  ))}
                                </div>

                                {/* Stuur tegenbod */}
                                {counterModalItemId !== item.id ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCounterModalItemId(item.id);
                                      setCounterSlotInputs([
                                        { date: '', startTime: '', endTime: '' },
                                        { date: '', startTime: '', endTime: '' },
                                        { date: '', startTime: '', endTime: '' },
                                      ]);
                                    }}
                                    className="w-full text-center text-[11px] font-medium text-gray-500 hover:text-gray-700 py-1 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                                  >
                                    ↩ Stuur tegenbod
                                  </button>
                                ) : (
                                  <div className="bg-white rounded-xl border border-gray-200 p-3 space-y-2">
                                    <div className="text-[11px] font-semibold text-gray-700 mb-1">
                                      Jouw tegenvoorstel (max. 3 tijdsblokken)
                                    </div>
                                    {counterSlotInputs.map((slot, idx) => (
                                      <div key={idx} className="grid grid-cols-3 gap-1.5">
                                        <div>
                                          <label className="block text-[9px] text-gray-500 mb-0.5">Datum</label>
                                          <input
                                            type="date"
                                            value={slot.date}
                                            onChange={(e) => {
                                              const updated = [...counterSlotInputs];
                                              updated[idx] = { ...updated[idx], date: e.target.value };
                                              setCounterSlotInputs(updated);
                                            }}
                                            className="w-full text-[10px] px-1.5 py-1 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-[9px] text-gray-500 mb-0.5">Van</label>
                                          <input
                                            type="time"
                                            value={slot.startTime}
                                            onChange={(e) => {
                                              const updated = [...counterSlotInputs];
                                              updated[idx] = { ...updated[idx], startTime: e.target.value };
                                              setCounterSlotInputs(updated);
                                            }}
                                            className="w-full text-[10px] px-1.5 py-1 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-[9px] text-gray-500 mb-0.5">Tot</label>
                                          <input
                                            type="time"
                                            value={slot.endTime}
                                            onChange={(e) => {
                                              const updated = [...counterSlotInputs];
                                              updated[idx] = { ...updated[idx], endTime: e.target.value };
                                              setCounterSlotInputs(updated);
                                            }}
                                            className="w-full text-[10px] px-1.5 py-1 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                                          />
                                        </div>
                                      </div>
                                    ))}
                                    <div className="flex gap-2 mt-2">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleSendCounter(item); }}
                                        disabled={sendingCounter}
                                        className="flex-1 bg-[#7A9488] text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#4F6660] transition-colors disabled:opacity-50"
                                      >
                                        {sendingCounter ? 'Versturen…' : 'Verstuur tegenbod'}
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setCounterModalItemId(null); }}
                                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 border border-gray-200 transition-colors"
                                      >
                                        Annuleer
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ── Handled new-proposal (accepted/confirmed) ── */}
                            {(item.type === 'proposal_received' || item.type === 'counter_received') && item.metadata?.status === 'handled' && (
                              <div className={`mt-2 p-3 rounded-lg border ${
                                item.metadata?.acceptedSlot ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                              }`}>
                                {item.metadata?.acceptedSlot ? (
                                  <>
                                    <div className="text-xs text-green-600 font-semibold mb-1">✓ Afspraak bevestigd</div>
                                    <div className="text-sm text-green-800 font-semibold">
                                      {formatRangeNl(
                                        new Date(item.metadata.acceptedSlot.start),
                                        new Date(item.metadata.acceptedSlot.end)
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-xs text-gray-500">Afgehandeld</div>
                                )}
                              </div>
                            )}

                            {/* Show handled status for proposals */}
                            {item.type === 'meeting_proposal' && item.metadata?.status === 'handled' && (
                              <div className={`mt-2 p-3 rounded-lg ${
                                item.metadata?.acceptedSlot 
                                  ? 'bg-green-50 border border-green-200' 
                                  : 'bg-red-50 border border-red-200'
                              }`}>
                                {item.metadata?.acceptedSlot ? (
                                  <>
                                    <div className="text-xs text-green-600 font-medium mb-1">
                                      Afspraak bevestigd
                                    </div>
                                    <div className="text-sm text-green-800 font-semibold">
                                      {formatRangeNl(
                                        new Date(item.metadata.acceptedSlot.start),
                                        new Date(item.metadata.acceptedSlot.end)
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-xs text-red-600 font-medium">
 Voorstel afgewezen
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Actions - Right side, only visible on hover */}
                          <div className="flex-shrink-0 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* Group Invitation — Accept / Decline */}
                            {item.type === 'group_invitation' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAcceptGroupInvitation(item); }}
                                  disabled={processingInvitation === item.id}
                                  className="bg-emerald-500 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50"
                                >
                                  {processingInvitation === item.id ? '...' : '✓ Accepteren'}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeclineGroupInvitation(item); }}
                                  disabled={processingInvitation === item.id}
                                  className="bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                                >
                                  Weigeren
                                </button>
                              </div>
                            )}

                            {/* Meeting Change Request - Show response buttons */}
                            {item.category === 'meeting' && item.title.includes('wijzigingsverzoek') && item.actionData?.changeRequestId && (
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRespondingToChange(item);
                                    setChangeResponse({ message: '', accept: true });
                                    setShowChangeResponseModal(true);
                                  }}
                                  className="bg-green-500 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-green-600 transition-colors"
                                >
                                  Accepteren
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRespondingToChange(item);
                                    setChangeResponse({ message: '', accept: false });
                                    setShowChangeResponseModal(true);
                                  }}
                                  className="bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-red-100 transition-colors"
                                >
 Weigeren
                                </button>
                              </div>
                            )}
                            
                            {item.actionData && !(item.category === 'meeting' && item.title.includes('wijzigingsverzoek')) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAction(item);
                                }}
                                className="bg-blue-500 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-blue-600 transition-colors"
                              >
                                {item.actionData.actionText || 'Actie'}
                              </button>
                            )}
                            
                            {!item.isRead && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(item.id);
                                }}
                                className="text-gray-400 hover:text-blue-600 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                                title="Markeer als gelezen"
                              >
                                Bekijk
                              </button>
                            )}
                            
                            {item.isArchived ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  unarchiveItem(item.id);
                                }}
                                className="text-gray-400 hover:text-green-700 p-1.5 rounded-md hover:bg-green-50 transition-colors text-xs"
                                title="Terugzetten naar inbox"
                              >
                                Terugzetten
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archiveItem(item.id);
                                }}
                                className="text-gray-400 hover:text-amber-700 p-1.5 rounded-md hover:bg-amber-50 transition-colors text-xs"
                                title="Archiveren"
                              >
                                Archiveer
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteItem(item.id);
                              }}
                              className="text-gray-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                              title="Verwijderen"
                            >
                              Verwijder
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )}
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Item Detail Modal */}
      {showItemModal && selectedItem ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span>{getItemIcon(selectedItem)}</span>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedItem.title}</h3>
                  <p className="text-sm text-gray-600">{getTimeAgo(selectedItem.createdAt)}</p>
                </div>
              </div>
              <button
                onClick={() => setShowItemModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Bericht</h4>
                <p className="text-gray-700">{selectedItem.message}</p>
              </div>

              {selectedItem.type === 'summary' && selectedItem.metadata?.callDuration && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Call Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Duur:</span>
                      <span className="ml-2">{selectedItem.metadata?.callDuration} minuten</span>
                    </div>
                    {selectedItem.metadata?.participants && (
                      <div>
                        <span className="font-medium">Deelnemers:</span>
                        <span className="ml-2">{selectedItem.metadata?.participants?.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {/* Mock AI Summary Data */}
                  <div className="mt-4 space-y-3">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Belangrijkste Punten:</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Bespreking van Q1 doelstellingen en KPI's</li>
                        <li>• Nieuwe marketingstrategie voor social media</li>
                        <li>• Budget allocatie voor aankomende projecten</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Actiepunten:</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Sarah: Marketingplan opstellen (deadline: vrijdag)</li>
                        <li>• Team: Budget voorstel voorbereiden</li>
                        <li>• Follow-up meeting plannen voor volgende week</li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Vervolgstappen:</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Review van marketingplan in volgende meeting</li>
                        <li>• Budget goedkeuring aanvragen bij management</li>
                        <li>• Implementatie timeline opstellen</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {selectedItem.actionData && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleAction(selectedItem!)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    {selectedItem.actionData!.actionText || 'Actie Uitvoeren'}
                  </button>
                  <button
                    onClick={() => setShowItemModal(false)}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                  >
                    Sluiten
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      
      {/* Meeting Change Response Modal */}
      {showChangeResponseModal && respondingToChange ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {changeResponse.accept ? 'Wijziging Accepteren' : 'Wijziging Weigeren'}
              </h3>
              <button
                onClick={() => {
                  setShowChangeResponseModal(false);
                  setRespondingToChange(null);
                  setChangeResponse({ message: '', accept: false });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="text-sm text-gray-700 mb-2">
                  <strong>Huidige tijd:</strong> {respondingToChange!.metadata?.oldStartTime 
                    ? new Date(respondingToChange!.metadata!.oldStartTime).toLocaleString('nl-NL')
                    : 'Onbekend'}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Nieuwe tijd:</strong> {respondingToChange!.metadata?.newStartTime 
                    ? new Date(respondingToChange!.metadata!.newStartTime).toLocaleString('nl-NL')
                    : 'Onbekend'}
                </div>
                {respondingToChange!.metadata?.reason && (
                  <div className="text-sm text-gray-600 mt-2 italic">
                    "{respondingToChange!.metadata!.reason}"
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {changeResponse.accept ? 'Bericht (optioneel)' : 'Reden voor weigering'}
                </label>
                <textarea
                  value={changeResponse.message}
                  onChange={(e) => setChangeResponse(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={changeResponse.accept 
                    ? "Voeg een bericht toe (optioneel)..." 
                    : "Leg uit waarom je de wijziging weigert..."}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488]"
                />
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={async () => {
                    try {
                      const meetingId = respondingToChange!.actionData?.meetingId;
                      const changeRequestId = respondingToChange!.actionData?.changeRequestId;
                      
                      if (!meetingId) {
                        alert(' Meeting ID niet gevonden');
                        return;
                      }

                      // Get meeting event
                      const meetingDoc = await getDoc(doc(db, 'calendarEvents', meetingId));
                      if (!meetingDoc.exists()) {
                        alert(' Meeting niet gevonden');
                        return;
                      }

                      const meetingData = meetingDoc.data();
                      const pendingChangeRequest = meetingData.pendingChangeRequest;

                      if (changeResponse.accept) {
                        // Accept change - update meeting time
                        await updateDoc(doc(db, 'calendarEvents', meetingId), {
                          startTime: pendingChangeRequest.newStartTime,
                          endTime: pendingChangeRequest.newEndTime,
                          'pendingChangeRequest.status': 'accepted',
                          updatedAt: new Date()
                        });

                        // Create inbox notification for requester
                        const requesterId = pendingChangeRequest.fromUserId;
                        await addDoc(collection(db, 'inboxItems'), {
                          userId: requesterId,
                          type: 'confirmation',
                          category: 'meeting',
                          title: 'Meeting wijziging geaccepteerd',
                          message: `${userProfile?.displayName || 'Iemand'} heeft je wijzigingsverzoek geaccepteerd.${changeResponse.message ? `\n\nBericht: ${changeResponse.message}` : ''}`,
                          isRead: false,
                          priority: 'high',
                          createdAt: new Date(),
                          metadata: {
                            eventDate: pendingChangeRequest.newStartTime?.toDate ? pendingChangeRequest.newStartTime.toDate() : new Date(pendingChangeRequest.newStartTime)
                          }
                        });

                        alert('Wijziging geaccepteerd en meeting bijgewerkt!');
                      } else {
                        // Decline change
                        await updateDoc(doc(db, 'calendarEvents', meetingId), {
                          'pendingChangeRequest.status': 'declined',
                          updatedAt: new Date()
                        });

                        // Create inbox notification for requester
                        const requesterId = pendingChangeRequest.fromUserId;
                        await addDoc(collection(db, 'inboxItems'), {
                          userId: requesterId,
                          type: 'notification',
                          category: 'meeting',
                          title: ' Meeting wijziging geweigerd',
                          message: `${userProfile?.displayName || 'Iemand'} heeft je wijzigingsverzoek geweigerd.${changeResponse.message ? `\n\nReden: ${changeResponse.message}` : ''}`,
                          isRead: false,
                          priority: 'medium',
                          createdAt: new Date()
                        });

                        alert(' Wijziging geweigerd');
                      }

                      // Mark inbox item as read
                      await markAsRead(respondingToChange!.id);
                      
                      // Close modal
                      setShowChangeResponseModal(false);
                      setRespondingToChange(null);
                      setChangeResponse({ message: '', accept: false });
                      
                      // Reload inbox
                      loadInboxData();
                    } catch (error) {
                      console.error('Error responding to change request:', error);
                      alert(' Fout bij verwerken wijzigingsverzoek');
                    }
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    changeResponse.accept
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  {changeResponse.accept ? 'Accepteren & Opslaan' : 'Weigeren & Verzenden'}
                </button>
                <button
                  onClick={() => {
                    setShowChangeResponseModal(false);
                    setRespondingToChange(null);
                    setChangeResponse({ message: '', accept: false });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Scroll Indicator */}
      <ScrollIndicator />
    </div>
  );
};

export default InboxPage;








